import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  TextField,
  Typography,
  InputAdornment,
  Button,
  ListItemButton,
  CircularProgress,
  IconButton,
  Tooltip,
  Grid,
} from "@mui/material";
import { Search, Refresh } from "@mui/icons-material";
import Auth from "../../services/auth";
import ExternalAuth from "../../services/externalAuth";
import {
  List,
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
} from "react-virtualized";
import { redirectTo } from "../../utils/redirectTo";
import { config } from "../../app/config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import toastMessage from "../../utils/toastMessage";
import { useAuth } from "../../hooks/useAuth";
import {normalizeAllSpacesAccentsSpecialCharacter} from "../../utils/wordsNormalizer";

export function SelecaoConvenio() {
  const [convenios, setConvenios] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [initialConveniosData, setInitialConveniosData] = useState([]);
  const [loadingMessage, setLoadingMessage] = useState("");

  const {
    userLogin,
    passwordLogin,
    isAuthenticated,
    handleLogout,
    storageDelete,
    hasTwoFactorAuth,
    clearance,
  } = useAuth();


  const cache = useRef(
    new CellMeasurerCache({
      fixedWidth: true,
      minHeight: 50,
    })
  );

  const onSearch = (searchInput) => {
    if (searchInput === "") {
      setConvenios(initialConveniosData);
    }

    const normalizedSearch = normalizeAllSpacesAccentsSpecialCharacter(searchInput);
    const filteredItems = initialConveniosData.filter(({ description, cnpjs, nomeInstituicao, codigosConvenente }) => {
      const normalizedDescription = normalizeAllSpacesAccentsSpecialCharacter(description)
      return (normalizedDescription.includes(normalizedSearch) ||
          (cnpjs && cnpjs.some(cnpj => cnpj.match(normalizedSearch))) ||
          (nomeInstituicao && normalizeAllSpacesAccentsSpecialCharacter(nomeInstituicao).includes(normalizedSearch)) ||
          (codigosConvenente && codigosConvenente.some(codigoConvenente => codigoConvenente.match(normalizedSearch))))
    });

    setConvenios(filteredItems);
  };

  const fetchConvenios = async () => {
    setIsLoading(true);
    setLoadingMessage("Aguarde, estamos carregando os convênios.");
    try {
      const { data } = await Auth.getContexts();
      await Auth.session();
      setConvenios(data);
      setInitialConveniosData(data);
      setIsLoading(false);
    } catch (error) {
      storageDelete();
      window.location.assign("/");
      toastMessage.toastMessageError(error);
      setIsLoading(false);
    }
  };

  const fetchValidations = async () => {
    const isValidate = await getValidateCode();

    if (isValidate) {
      fetchConvenios();
      return;
    }

    storageDelete();
    setIsLoading(false);
    handleLogout();
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchValidations();
    }
  }, []);

  async function getValidateCode() {
    if (!hasTwoFactorAuth) return true;
    try {
      const { ok } = await Auth.getValidateCode();

      if (!ok) {
        toast.error("Validação autenticação dois fatores pendente.", {
          theme: "colored",
          closeButton: false,
        });
        return false;
      }

      return true;
    } catch (err) {
      toastMessage.toastMessageError();
    }
  }

  async function handleSelect(convenioId) {
    setIsLoading(true);
    setLoadingMessage("Em alguns instantes, você será redirecionado.");

    const { ok, data } = await Auth.getContextType(convenioId);
    if (!ok || data === undefined) {
      setIsLoading(false);
      return toastMessage.toastMessageError(data);
    }
    const classificacaoInstituicao = data;

    const type =
      classificacaoInstituicao === "PUBLICA" ? "consignet" : "mixtra";

    const loginResponse = await ExternalAuth.login(
      classificacaoInstituicao,
      type,
      userLogin,
      passwordLogin,
      clearance
    );

    if (loginResponse.status !== 200 || !loginResponse.data) {
      setIsLoading(false);
      toastMessage.toastMessageError(loginResponse);
      setTimeout(handleLogout, 5000);
      return;
    }

    const selectResponse = await ExternalAuth.select(
      convenioId,
      loginResponse.data.id,
      type,
      classificacaoInstituicao,
      userLogin,
      passwordLogin,
      clearance
    );

    if (!selectResponse.ok) {
      return toastMessage.toastMessageError(selectResponse);
    }

    if (config.all.isDev) {
      redirectTo[config.all.validateEnvironment]("admin/home");
    }
    if (config.all.validateEnvironment === "HOMOLOG") {
      redirectTo[config.all.validateEnvironment](
        type,
        config.all.validateEnvironmentNumber,
        "admin/home"
      );
    }

    if (config.all.validateEnvironment === "IMPLANTACAO") {
      redirectTo[config.all.validateEnvironment](type, "admin/home");
    }

    if (config.all.validateEnvironment === "PRODUCTION") {
      redirectTo[classificacaoInstituicao]("admin/home");
    }

    setIsLoading(false);
  }

  const handleRefresh = async () => {
    const { status, data } = await Auth.redisRefresh();
    if (status === 200) {
      setConvenios([]);
      fetchConvenios();
    } else {
      toastMessage.toastMessageError(data);
    }
  };

  return (
    <>
      <ToastContainer />
      <Box
        sx={{
          width: "400px",
          boxShadow:
            "-4px -4px 10px rgba(0, 0, 0, 0.07), 4px 4px 10px rgba(0, 0, 0, 0.4)",
          backgroundColor: "#7A8A93",
          margin: "10%",
          borderRadius: "8px",
        }}
      >
        <Typography
          variant="h6"
          color="white"
          textAlign="center"
          px={4}
          pt={4}
          sx={{
            fontWeight: "normal",
          }}
        >
          SELECIONE UM CONVÊNIO
        </Typography>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            marginTop: "20px",
            padding: "0 26px",
          }}
        >
          <TextField
            fullWidth
            disabled={isLoading}
            variant="standard"
            placeholder="PESQUISAR"
            onChange={({ target }) =>
              onSearch(target.value.toLocaleLowerCase())
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ pb: 0.5 }}>
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{
              width: "100%",
              textTransform: "uppercase",
              color: "white",
              "& input": {
                pb: 0.5,
                color: "white",
                fontSize: "16px",
              },
              "& input::placeholder": { color: "white", opacity: 0.6 },
              "& .MuiInput-root:hover:not(.Mui-disabled):before": {
                borderColor: "white",
              },
            }}
          />
          <Grid item justifyContent="center" alignItems="center">
            <Tooltip title="Atualizar">
              <IconButton
                disabled={isLoading}
                onClick={handleRefresh}
                sx={{
                  color: "white",
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Grid>
        </Box>
        <Box width="100%" height="360px" backgroundColor="#FFFFFF" mt="55px">
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <Typography
                color="#414649"
                textAlign="center"
                sx={{
                  fontWeight: "normal",
                  marginBottom: "12px",
                }}
              >
                {loadingMessage}
              </Typography>
              <CircularProgress color="inherit" />
            </Box>
          ) : (
            <AutoSizer>
              {({ width, height }) => (
                <List
                  disableHeight
                  width={width}
                  height={height}
                  rowHeight={cache.current.rowHeight}
                  deferredMeasurementCache={cache.current}
                  rowCount={convenios.length}
                  rowRenderer={({ key, index, style, parent }) => {
                    const convenio = convenios[index];

                    return (
                      <CellMeasurer
                        key={key}
                        cache={cache.current}
                        parent={parent}
                        rowIndex={index}
                        columnIndex={0}
                      >
                        <ListItemButton
                          style={style}
                          disabled={convenio.classification.length <= 4}
                          onClick={() =>
                            handleSelect(
                              convenio.id,
                              convenio.classificacaoInstituicao
                            )
                          }
                          sx={{
                            marginBottom: "16px",
                            paddingLeft: "45px",
                            color: "#7A8A93",
                            "&:hover": {
                              color: "#414649",
                            },
                          }}
                        >
                          {convenio.description}
                        </ListItemButton>
                      </CellMeasurer>
                    );
                  }}
                />
              )}
            </AutoSizer>
          )}
        </Box>

        <Box
          component="footer"
          display="flex"
          alignItems="center"
          justifyContent="center"
          py="14px"
        >
          <Button
            disabled={isLoading}
            onClick={handleLogout}
            variant="text"
            sx={{
              color: "#FFFFFF",
              fontWeight: "normal",
              "&:hover": {
                opacity: "0.8",
                backgroundColor: "transparent",
              },
            }}
          >
            VOLTAR PARA TELA DE LOGIN
          </Button>
        </Box>
      </Box>
    </>
  );
}
